import { IPersonaProps } from "@fluentui/react";
import { validateEmail } from "../src/taskpane/components/helpers/validateEmail";
import { ADVANCED_SEARCH_CONCURRENCY, mapWithConcurrency } from "./advancedSearchRequests";

// People in Advanced Search. A person column stores a SITE user id, and the same person has a
// different id in every site collection. The picker therefore lists each person once, keyed by
// email, and a search translates the chosen people into each library's own site ids: sending one
// site's id to another site would match a different person there, or nobody.

/** A row of a site's /siteusers, as getSharePointSiteUsers returns it. */
export interface ISiteUser {
  Id: number;
  Title?: string;
  Email?: string;
  UserPrincipalName?: string;
}

/** What the picker shows as the person's second line: the email, else a mail-shaped user
 * principal name. Accounts with neither (system and app accounts) are never listed. */
const personMail = (user: ISiteUser | undefined): string | undefined => {
  const email: string = (user?.Email ?? "").trim();
  if (email) {
    return email;
  }
  const principal: string = (user?.UserPrincipalName ?? "").trim();
  return validateEmail(principal) ? principal : undefined;
};

/** The key a person is known by across sites: their mail, lowercased. */
export const personKey = (user: ISiteUser | undefined): string | undefined => personMail(user)?.toLowerCase();

/** One persona per person across the sites' users, in the order first met; its id is the person
 * key. A site whose users could not be read (undefined) adds nobody. */
export const mergeSitePeople = (sitesUsers: (ISiteUser[] | undefined)[]): IPersonaProps[] => {
  const people: IPersonaProps[] = [];
  const seen: Set<string> = new Set<string>();
  (sitesUsers ?? []).forEach(users => (users ?? []).forEach(user => {
    const mail: string | undefined = personMail(user);
    const key: string | undefined = mail?.toLowerCase();
    if (key && !seen.has(key)) {
      seen.add(key);
      people.push({ id: key, text: user.Title, secondaryText: mail });
    }
  }));
  return people;
};

/** The site user ids the people behind `keys` have on one site, in key order. */
export const siteUserIdsFor = (keys: string[], users: ISiteUser[]): number[] => {
  const ids: number[] = [];
  (keys ?? []).forEach(key => {
    const wanted: string = (key ?? "").trim().toLowerCase();
    (users ?? []).forEach(user => {
      if (wanted && personKey(user) === wanted && typeof user.Id === "number" && ids.indexOf(user.Id) === -1) {
        ids.push(user.Id);
      }
    });
  });
  return ids;
};

const chosenKeys = (value: any): string[] =>
  (Array.isArray(value) ? value : [value]).filter(key => typeof key === "string" && key.trim().length > 0);

/**
 * The search inputs for one library's site: every people input's chosen people replaced by their
 * ids on that site; other inputs are returned as they are. Undefined when a people input names
 * nobody the site knows (no item there can match, so the library is not searched) or when the
 * site's users could not be read (its ids are unknown).
 */
export const peopleInputsForSite = <T extends { value?: any }>(
  inputs: T[],
  isPeopleInput: (input: T) => boolean,
  users: ISiteUser[] | undefined,
): T[] | undefined => {
  const list: T[] = inputs ?? [];
  let unmatched: boolean = false;
  const translated: T[] = list.map(input => {
    if (!isPeopleInput(input)) {
      return input;
    }
    const keys: string[] = chosenKeys(input.value);
    if (keys.length === 0) {
      return input;
    }
    const ids: number[] = users ? siteUserIdsFor(keys, users) : [];
    if (ids.length === 0) {
      unmatched = true;
      return input;
    }
    return { ...input, value: ids };
  });
  return unmatched ? undefined : translated;
};

const siteKey = (webUrl: string | undefined): string => (webUrl ?? "").trim().replace(/\/+$/, "").toLowerCase();

/** Each site's users, read once per open and shared by the picker and every search. An empty or
 * failed read is not kept - a site always has users - so the next use tries again. */
export class SiteUsersCache {
  private readonly loads: Map<string, Promise<ISiteUser[] | undefined>> = new Map<string, Promise<ISiteUser[] | undefined>>();

  constructor(
    private readonly fetchUsers: (webUrl: string) => Promise<ISiteUser[] | undefined>,
    private readonly limit: number = ADVANCED_SEARCH_CONCURRENCY,
  ) { }

  load(webUrl: string): Promise<ISiteUser[] | undefined> {
    const key: string = siteKey(webUrl);
    if (!key) {
      return Promise.resolve(undefined);
    }
    const cached: Promise<ISiteUser[] | undefined> | undefined = this.loads.get(key);
    if (cached) {
      return cached;
    }
    const forget = (): undefined => {
      if (this.loads.get(key) === pending) {
        this.loads.delete(key);
      }
      return undefined;
    };
    const pending: Promise<ISiteUser[] | undefined> = Promise.resolve()
      .then(() => this.fetchUsers(webUrl))
      .then(users => Array.isArray(users) && users.length > 0 ? users : forget(), forget);
    this.loads.set(key, pending);
    return pending;
  }

  /** Several sites, a few at a time, in the order given. Never rejects: load does not. */
  loadAll(webUrls: string[]): Promise<(ISiteUser[] | undefined)[]> {
    return mapWithConcurrency(webUrls ?? [], this.limit, webUrl => this.load(webUrl));
  }
}
